// Monotone cubic interpolation (Fritsch–Carlson 1980), baked to 1024-entry LUTs (§6.4).
export type Pt = { x: number; y: number };
export const LUT_SIZE = 1024;
export const IDENTITY_CURVE: Pt[] = [
  { x: 0, y: 0 },
  { x: 1, y: 1 },
];

export function isIdentity(pts: Pt[]): boolean {
  return pts.every((p) => Math.abs(p.x - p.y) < 1e-9);
}

/** Sorted, de-duplicated, clamped points that always include x = 0 and x = 1. */
export function normalizePoints(pts: Pt[]): Pt[] {
  const c = (v: number) => Math.min(1, Math.max(0, v));
  const m = new Map<number, number>();
  for (const p of pts) m.set(c(p.x), c(p.y));
  if (!m.has(0)) m.set(0, 0);
  if (!m.has(1)) m.set(1, 1);
  return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([x, y]) => ({ x, y }));
}

/** Returns an evaluator for the monotone cubic through `pts` (monotone wherever the data is). */
export function monotoneCubic(input: Pt[]): (x: number) => number {
  const p = normalizePoints(input);
  const n = p.length;
  const dx: number[] = [];
  const s: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(p[i + 1]!.x - p[i]!.x);
    s.push((p[i + 1]!.y - p[i]!.y) / dx[i]!);
  }
  const m: number[] = new Array(n).fill(0);
  m[0] = s[0]!;
  m[n - 1] = s[n - 2]!;
  for (let i = 1; i < n - 1; i++) m[i] = s[i - 1]! * s[i]! <= 0 ? 0 : (s[i - 1]! + s[i]!) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (s[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i]! / s[i]!;
    const b = m[i + 1]! / s[i]!;
    const h = a * a + b * b;
    if (h > 9) {
      const t = 3 / Math.sqrt(h);
      m[i] = t * a * s[i]!;
      m[i + 1] = t * b * s[i]!;
    }
  }
  return (x: number) => {
    if (x <= p[0]!.x) return p[0]!.y;
    if (x >= p[n - 1]!.x) return p[n - 1]!.y;
    let i = 0;
    while (i < n - 2 && x > p[i + 1]!.x) i++;
    const h = dx[i]!;
    const t = (x - p[i]!.x) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    return (
      (2 * t3 - 3 * t2 + 1) * p[i]!.y +
      (t3 - 2 * t2 + t) * h * m[i]! +
      (-2 * t3 + 3 * t2) * p[i + 1]!.y +
      (t3 - t2) * h * m[i + 1]!
    );
  };
}

export function bakeLut(pts: Pt[], size = LUT_SIZE): Float32Array {
  const f = monotoneCubic(pts);
  const lut = new Float32Array(size);
  for (let i = 0; i < size; i++) lut[i] = Math.min(1, Math.max(0, f(i / (size - 1))));
  return lut;
}
